///pie chart
import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { reaction } from "mobx";
import styles from "../../styles/dashboard.module.css";
import loadingSvg from "../../../Assets/images/tube-spinner.svg";
import { styled } from "@mui/material/styles";
import { useDrawingArea } from "@mui/x-charts"; // 從主包導入
import { pieArcLabelClasses, PieChart } from "@mui/x-charts/PieChart";
import dashboardStore from "../../store/dashboardStore";
import { useTranslation } from "react-i18next";
import {
  useItemTooltip,
  ChartsTooltipContainer,
} from "@mui/x-charts/ChartsTooltip";

const YTDChart = observer(() => {
  const { t } = useTranslation("ActivityMain");

  useEffect(() => {
    const dispose = reaction(
      () => [
        dashboardStore.selectedCom,
        dashboardStore.selectedYear,
        dashboardStore.selectedMonth,
        dashboardStore.gwpId,
        dashboardStore.unit,
        dashboardStore.standardId,
      ],
      () => {
        if (
          !dashboardStore.selectedCom ||
          dashboardStore.selectedCom.length === 0
        )
          return;

        if (!dashboardStore.selectedYear) {
          return;
        }

        dashboardStore.getPieChartData();
      },
      {
        fireImmediately: true,
      },
    );

    return () => dispose();
  }, []);

  const dataForScope = [
    {
      id: 0,
      value: dashboardStore.pieChartData.scope1?.value || 0,
      percentage: dashboardStore.pieChartData.scope1?.percentage || 0,
      label: `${t("Scope")} 1`,
      color: dashboardStore.themeColor[0],
    },
    {
      id: 1,
      value: dashboardStore.pieChartData.scope2?.value || 0,
      percentage: dashboardStore.pieChartData.scope2?.percentage || 0,
      label: `${t("Scope")} 2`,
      color: dashboardStore.themeColor[1],
    },
    {
      id: 2,
      value: dashboardStore.pieChartData.scope3?.value || 0,
      percentage: dashboardStore.pieChartData.scope3?.percentage || 0,
      label: `${t("Scope")} 3`,
      color: dashboardStore.themeColor[2],
    },
  ];

  const dataForCat = [
    {
      id: 0,
      value: dashboardStore.pieChartData.category1?.value || 0,
      percentage: dashboardStore.pieChartData.category1?.percentage || 0,
      label: `${t("Category")} 1`,
      color: dashboardStore.themeColor[0],
    },
    {
      id: 1,
      value: dashboardStore.pieChartData.category2?.value || 0,
      percentage: dashboardStore.pieChartData.category2?.percentage || 0,
      label: `${t("Category")} 2`,
      color: dashboardStore.themeColor[1],
    },
    {
      id: 2,
      value: dashboardStore.pieChartData.category3?.value || 0,
      percentage: dashboardStore.pieChartData.category3?.percentage || 0,
      label: `${t("Category")} 3`,
      color: dashboardStore.themeColor[2],
    },
    {
      id: 3,
      value: dashboardStore.pieChartData.category4?.value || 0,
      percentage: dashboardStore.pieChartData.category4?.percentage || 0,
      label: `${t("Category")} 4`,
      color: dashboardStore.themeColor[3],
    },
    {
      id: 4,
      value: dashboardStore.pieChartData.category5?.value || 0,
      percentage: dashboardStore.pieChartData.category5?.percentage || 0,
      label: `${t("Category")} 5`,
      color: dashboardStore.themeColor[4],
    },
    {
      id: 5,
      value: dashboardStore.pieChartData.category6?.value || 0,
      percentage: dashboardStore.pieChartData.category6?.percentage || 0,
      label: `${t("Category")} 6`,
      color: dashboardStore.themeColor[5],
    },
  ];

  //圓餅圖上的百分比標籤
  const getArcLabel = (item) => {
    return item.percentage === 0 ? "" : `${item.percentage}%`;
  };

  // 添加 StyledText 組件
  const StyledText = styled("text")(({ theme }) => ({
    fill: theme.palette.text.primary,
    textAnchor: "middle",
    dominantBaseline: "central",
    fontSize: 20,
  }));

  // 修改 PieCenterLabel 組件，讓數字變大
  function PieCenterLabel({ children }) {
    const { width, height, left, top } = useDrawingArea();
    // 分離數值和單位
    // 檢查children是否為字符串或數字
    const value =
      typeof children === "object"
        ? children[0]
        : parseFloat(children).toFixed(4);

    const unit = typeof children === "object" ? children[1] : unitLabel;

    return (
      <StyledText x={left + width / 2} y={top + height / 2 - 12}>
        {/* 數值部分 */}
        <tspan
          x={left + width / 2}
          dy={0}
          fontSize={24}
          // fontWeight="bold"
        >
          {value}
        </tspan>
        {/* 單位部分 */}
        <tspan
          x={left + width / 2}
          dy="1.5em"
          fontSize={18}
          fontWeight="normal"
        >
          {unit}
        </tspan>
      </StyledText>
    );
  }

  //判斷目前單位
  const unitLabel =
    dashboardStore.unit === "20"
      ? "kgCO2e"
      : dashboardStore.unit === "21"
        ? "tCO2e"
        : "";

  const CustomTooltip = () => {
    const tooltipData = useItemTooltip();

    if (!tooltipData) return null;

    // 新版結構：直接從 tooltipData 取得資料
    const { color, label, value, identifier } = tooltipData;

    // percentage 需要從原始 chartData 比對取得
    const currentItem = chartData.find(
      (item) => item.id === identifier?.dataIndex,
    );
    const percentage = currentItem?.percentage ?? 0;

    return (
      <ChartsTooltipContainer trigger="item">
        <div className={styles.tooltip}>
          <div className={styles.tooltipTitle}>
            <span
              className={styles.colorBox}
              style={{ backgroundColor: currentItem.color }}
            />
            {t(currentItem.label)}
          </div>
          <table className={styles.table}>
            <tbody>
              <tr>
                <td>{t("Emission Value")}</td>
                <td className={styles.right}>
                  {Number(currentItem.value).toLocaleString()}
                </td>
              </tr>
              <tr>
                <td></td>
                <td className={styles.right}>{unitLabel}</td>
              </tr>
              <tr>
                <td>{t("Percentage")}</td>
                <td className={styles.right}>{currentItem.percentage} %</td>
              </tr>
            </tbody>
          </table>
        </div>
      </ChartsTooltipContainer>
    );
  };

  //判斷Value是不是都是0
  const isAllZero = (data) => {
    return data.every((item) => item.value === 0);
  };

  //判斷是哪個類別或是範疇
  const chartData =
    dashboardStore.groupBy === "scope" ? dataForScope : dataForCat;

  return (
    <div className={`${styles.chartContent} ${styles.grid_4}`}>
      <div className={styles.chartTitle}>
        <span>{dashboardStore.selectedYear}</span>
        {dashboardStore.format === "monthly" ? (
          <span>{`/${String(dashboardStore.selectedMonth).padStart(
            2,
            "0",
          )}`}</span>
        ) : null}
        <span>{t("Emission Summary")}</span>
      </div>
      {/* <div className={styles.chartBody}> */}
      <div className={styles.chart}>
        {/**loading page */}
        {dashboardStore.pieChartDataLoading ? (
          <div className={styles.loading}>
            <img
              className={styles.loadingImg}
              src={loadingSvg}
              alt="loadingSvg"
            />
          </div>
        ) : isAllZero(chartData) ? (
          <div className={styles.noData}>{t("No data")}!</div>
        ) : (
          <PieChart
            series={[
              {
                outerRadius: 110,
                innerRadius: 90,
                data: chartData,
                // arcLabel: getArcLabel,
              },
            ]}
            // width={400}
            // height={250}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              [`& .${pieArcLabelClasses.root}`]: {
                fill: "dimgrey",
                fontSize: 14,
                fontWeight: "bold",
              },
              // 直接對 series 設定 fontSize（不透過 text 子元素）
              "& .MuiChartsLegend-series": {
                fontSize: "16px !important",
              },
              // legend 整體往左移
              "& .MuiChartsLegend-root": {
                transform: "translateX(-25px)",
              },
            }}
            slots={{
              tooltip: CustomTooltip, // ✅ 正確位置
            }}
            slotProps={{
              // legend: {
              //   fontSize:"16px",
              //    padding: { right: 40 },  // 往左移：增加右邊 padding，讓 legend 往左推
              // },
              legend: {
                itemMarkWidth: 16,
                itemMarkHeight: 16,
                markGap: 8,
                itemGap: 12,
              },
              tooltip: {
                trigger: "item",
              },
            }}
          >
            {/* 添加中心標籤 */}
            <PieCenterLabel>
              {[
                Number(
                  chartData.reduce((sum, item) => sum + item.value, 0),
                ).toLocaleString(undefined, {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                }),
                unitLabel,
              ]}
            </PieCenterLabel>
          </PieChart>
        )}
      </div>
    </div>
  );
});

export default YTDChart;
